import { useMemo, useState } from "react";
import { useSalonStore } from "@/lib/store/salon";
import { useNotificationMutation, useNotificationQuery, useNotificationSubscription } from "./core";

interface UseNotificationsOptions {
  first?: number;
}

export function useNotifications({ first = 20 }: UseNotificationsOptions = {}) {
  const { currentSalon } = useSalonStore();
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Core hooks
  const { data, loading, error, retry, refetch, fetchMore, hasMore } = useNotificationQuery({
    salonId: currentSalon?.id || null,
    first,
  });

  const { markAsRead, deleteNotifications } = useNotificationMutation();

  // Real-time subscription with cache integration
  const { isConnected } = useNotificationSubscription({
    salonId: currentSalon?.id || null,
    onNotificationReceived: (_notification) => {
      // Force refetch to get updated stats and ensure consistency
      refetch();
    },
    onStatsUpdated: () => {
      // Refetch when stats are updated from other sources
      refetch();
    },
    showToast: true,
  });

  // Transform data
  const notifications = useMemo(() => {
    if (!data?.getNotifications?.edges) return [];

    const notificationNodes = data.getNotifications.edges.map((edge) => edge.node);

    // Additional safety: remove duplicates by ID
    const uniqueNotifications = notificationNodes.filter(
      (notification, index, array) => array.findIndex((n) => n.id === notification.id) === index
    );

    return uniqueNotifications;
  }, [data]);

  // Load more function for cursor pagination
  const loadMore = async () => {
    if (!hasMore || loading || isLoadingMore) return;

    const lastEdge = data?.getNotifications?.edges?.slice(-1)[0];
    if (!lastEdge) return;

    setIsLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          after: lastEdge.cursor,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult?.getNotifications) {
            return previousResult;
          }

          const existingEdges = previousResult?.getNotifications?.edges || [];
          const newEdges = fetchMoreResult.getNotifications.edges;
          const newPageInfo = fetchMoreResult.getNotifications.pageInfo;

          // Create a Set of existing IDs to avoid duplicates
          const existingIds = new Set(existingEdges.map((edge) => edge.node.id));

          // Filter out any new edges that already exist
          const uniqueNewEdges = newEdges.filter((edge) => !existingIds.has(edge.node.id));

          return {
            getNotifications: {
              ...fetchMoreResult.getNotifications,
              edges: [...existingEdges, ...uniqueNewEdges],
              pageInfo: newPageInfo,
            },
          };
        },
      });
    } catch (error) {
      console.error("Error loading more notifications:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  return {
    // Data
    notifications,

    // Loading states
    loading,
    isLoadingMore,
    hasMore,

    // Connection status
    isConnected,

    // Error handling
    error,

    // Actions
    retry,
    refetch,
    loadMore,
    markAsRead,
    deleteNotifications,
  };
}
